import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import ModernServicesCarousel from './ModernServicesCarousel'
import supabase from '../lib/supabase'

const { FiArrowRight, FiCheck, FiStar, FiTrendingUp, FiGrid, FiEye, FiChevronDown, FiChevronUp } = FiIcons

function ServicesSection({ onContactClick }) {
  const [allServices, setAllServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAllServices, setShowAllServices] = useState(false)

  useEffect(() => {
    fetchAllServices()
  }, [])

  const fetchAllServices = async () => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('codevaro_services')
        .select(`
          *,
          codevaro_categories:category_id (
            id,
            name,
            slug
          )
        `)
        .eq('is_active', true)
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
      
      if (error) {
        console.error('Error fetching services:', error)
      } else {
        setAllServices(data || [])
      }
    } catch (error) {
      console.error('Error in fetchAllServices:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleContactClick = (category = null) => {
    if (onContactClick) {
      onContactClick(category)
    } else {
      // Fallback to scrolling to contact section
      const contactSection = document.getElementById('contact')
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  const toggleServicesView = () => {
    setShowAllServices(!showAllServices)
  }

  return (
    <section id="services" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Carousel */}
        <ModernServicesCarousel onContactClick={handleContactClick} />
        
        {/* Services Toggle Section */}
        {!loading && allServices.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-20"
          >
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Όλες οι <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Υπηρεσίες</span> μας
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto mb-8">
                Εξερευνήστε το πλήρες φάσμα των υπηρεσιών που προσφέρουμε για να καλύψουμε κάθε ανάγκη της επιχείρησής σας
              </p>
              
              {/* Fancy Reveal Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleServicesView}
                className="group relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white px-8 py-4 rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1"
              >
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-800 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                {/* Button Content */}
                <div className="relative flex items-center justify-center space-x-3">
                  <SafeIcon 
                    icon={showAllServices ? FiEye : FiGrid} 
                    className="h-5 w-5 group-hover:rotate-12 transition-transform duration-300" 
                  />
                  <span className="text-lg">
                    {showAllServices ? 'Απόκρυψη Υπηρεσιών' : 'Δείτε Όλες τις Υπηρεσίες'}
                  </span>
                  <SafeIcon 
                    icon={showAllServices ? FiChevronUp : FiChevronDown} 
                    className="h-5 w-5 group-hover:translate-y-1 transition-transform duration-300" 
                  />
                </div>
                
                {/* Sparkle Effect */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-2xl">
                  <div className="absolute top-1/2 left-1/2 w-0 h-0 bg-white/20 rounded-full group-hover:w-96 group-hover:h-96 transition-all duration-700 transform -translate-x-1/2 -translate-y-1/2"></div>
                </div>
              </motion.button>
            </div>
            
            {/* Services Grid with Animation */}
            <AnimatePresence>
              {showAllServices && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -50 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -50 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
                    {allServices.map((service, index) => (
                      <motion.div
                        key={service.id}
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -30, scale: 0.9 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group card-hover"
                      >
                        <div className="p-6">
                          {/* Category Badge */}
                          <div className="flex items-center justify-between mb-4">
                            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                              {service.codevaro_categories?.name || service.category || 'Χωρίς κατηγορία'}
                            </span>
                            {service.show_in_carousel && (
                              <span className="inline-flex items-center text-green-600 text-sm">
                                <SafeIcon icon={FiStar} className="h-4 w-4 mr-1" />
                                Προτεινόμενο
                              </span>
                            )}
                          </div>
                          
                          {/* Title */}
                          <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                            {service.title}
                          </h4>
                          
                          {/* Description */}
                          <p className="text-gray-600 mb-4 line-clamp-3">
                            {service.description}
                          </p>
                          
                          {/* Features */}
                          {service.features && service.features.length > 0 && (
                            <div className="mb-4">
                              {service.features.slice(0, 3).map((feature, featureIndex) => (
                                <div key={featureIndex} className="flex items-center text-sm text-gray-600 mb-1">
                                  <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mr-2" />
                                  {feature}
                                </div>
                              ))}
                              {service.features.length > 3 && (
                                <p className="text-sm text-gray-500 mt-2">
                                  +{service.features.length - 3} περισσότερα χαρακτηριστικά
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Price and Duration */}
                          <div className="flex items-center justify-between mb-6">
                            {service.price && (
                              <div className="text-2xl font-bold text-gray-900">
                                €{service.price}
                              </div>
                            )}
                            {service.duration && (
                              <div className="text-sm text-gray-500">
                                {service.duration}
                              </div>
                            )}
                          </div>
                          
                          {/* CTA Button */}
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleContactClick(service.codevaro_categories?.slug || service.codevaro_categories?.name || service.category)}
                            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg group-hover:from-blue-700 group-hover:to-purple-700 flex items-center justify-center"
                          >
                            Μάθετε περισσότερα
                            <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Bottom CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="text-center mt-12"
            >
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                <SafeIcon icon={FiTrendingUp} className="h-12 w-12 mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-4">
                  Έτοιμοι να αναπτύξετε την επιχείρησή σας;
                </h3>
                <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                  Επικοινωνήστε μαζί μας σήμερα για μια δωρεάν συμβουλευτική συνεδρία και ανακαλύψτε πως μπορούμε να σας βοηθήσουμε να επιτύχετε τους στόχους σας.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleContactClick()}
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold transition-all duration-300 hover:shadow-lg inline-flex items-center"
                >
                  Κλείστε Δωρεάν Ραντεβού
                  <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  )
}

export default ServicesSection